import React, { useEffect, useRef } from 'react';
import { ViewerCanvasWrapper } from '@components/pdf-reader/styled.ts';
import type { PDFDocumentProxy } from 'pdfjs-dist';

interface Props {
    pdf: PDFDocumentProxy;
    pageNumber: number;
    scale: number;
    rotation: number;
    onHeightReady?: (page: number, height: number) => void;
}

export const PdfPage: React.FC<Props> = ({ pdf, pageNumber, scale, rotation, onHeightReady }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        // Render the requested PDF page into this component's canvas whenever
        // the PDF instance, page number, scale, or rotation changes.
        const renderPage = async () => {
            try {
                // 1) Get the page proxy for the current page number.
                const page = await pdf.getPage(pageNumber);

                // 2) Compute the viewport for the given scale/rotation (defines pixel size).
                const viewport = page.getViewport({ scale, rotation });

                // 3) Resolve the canvas and its 2D context; abort if missing.
                const canvas = canvasRef.current;
                if (!canvas) return;
                const ctx = canvas.getContext('2d');
                if (!ctx) return;

                // 4) Match the canvas dimensions to the viewport so the render is crisp.
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                // 5) If a previous render is still running on this canvas, cancel it
                //    to prevent races/out-of-order finishes.
                // If this canvas already has an in-flight PDF.js render task (from a prior page/scale/rotation),
                // cancel it so two renders don’t race on the same canvas or finish out of order.
                // (We stash the RenderTask on the canvas as __pdfRenderTask; cancel() will cause a RenderingCancelledException.)
                if ((canvas as any).__pdfRenderTask) {
                    (canvas as any).__pdfRenderTask.cancel();
                }

                // 6) Start a new render task and stash it on the canvas for cancellation.
                const task = page.render({ canvas, canvasContext: ctx, viewport });
                (canvas as any).__pdfRenderTask = task;

                // 7) Wait until the rendering completes.
                await task.promise;

                // 8) Report the final rendered height to the parent ( +16 for wrapper spacing ).
                if (onHeightReady) {
                    onHeightReady(pageNumber, canvas.height + 16);
                }
            } catch (err: any) {
                // 9) Ignore expected cancellations; log other errors.
                if (err?.name !== 'RenderingCancelledException') {
                    console.error(`PDF render error on page ${pageNumber}:`, err);
                }
            }
        };

        // Kick off the async render (without awaiting inside the effect).
        void renderPage();

        return () => {
            if (canvasRef.current && (canvasRef.current as any).__pdfRenderTask) {
                (canvasRef.current as any).__pdfRenderTask.cancel();
            }
        };
    }, [pdf, pageNumber, scale, rotation]);

    return <ViewerCanvasWrapper ref={canvasRef} data-page={pageNumber} />;
};
